import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import Tabs from '../../components/atoms/Tabs';
import FilterBar from '../../components/FilterBar';
import SingleSelect from '../../components/atoms/forms/SingleSelect';
import MultiSelect from '../../components/atoms/forms/MultiSelect';
import DateRangeField from '../../components/atoms/forms/DateRangeField';
import DateField from '../../components/atoms/forms/DateField';
import { OpsLocations } from '../../libs/GraphQL';
import PlanningTab from './PlanningTab';
import RouteOperationsTab from './RouteOperationsTab';
import MonitoringTab from './MonitoringTab';
import ExceptionReviewTab from './ExceptionReviewTab';
import DispatchTab from './DispatchTab';
import StatusRefreshControl from './StatusRefreshControl';
import {
  TAB_IDS,
  TABS,
  REGIONS,
  REGION_ALL,
  DATE_RANGE_LIMIT_DAYS,
  DEFAULT_DATE_RANGE_DAYS,
  MONITORING_DATE_LOOKBACK_DAYS,
  DISPATCH_DATE_LOOKBACK_DAYS,
  SINGLE_DATE_LOOKAHEAD_DAYS,
} from './constants';
import { validateRange, getDefaultDateRange } from './utils';
import useDispatchTabFlag from './useDispatchTabFlag';
import { PRETTY_TOAST_OPTS as toastOpts } from '../../utils/toast';

const StyledDiv = styledComponent('div', {
  '& .sd-container': {
    // PageViewContainer (the route-level wrapper) already supplies the
    // 0 / 84px horizontal gutter; using `pagePadding` here doubled it,
    // making the Supervisor Dashboard ~168px narrower than the other
    // dashboards mounted under the same wrapper.
    padding: theme.spacing.pagePaddingVertical,
  },
  '& .sd-page-header-row': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.sectionGap,
    marginBottom: theme.spacing.pageTitleMargin,
    flexWrap: 'wrap',
  },
  '& .sd-page-header': {
    ...theme.typography.pageTitle,
    color: theme.colors.textDark,
    margin: 0,
  },
  '& .sd-tabs-row': {
    marginBottom: theme.spacing.sectionGap,
  },
  '& .sd-filter-row-wrapper': {
    marginBottom: theme.spacing.sectionGap,
  },
  '& .sd-input-wrapper': {
    display: 'flex',
    flexDirection: 'column',
    minWidth: theme.sizes.filterFieldMinWidth,
  },
  '& .sd-region-wrapper': {
    width: theme.sizes.regionSelectWidth,
  },
  '& .sd-market-wrapper': {
    width: theme.sizes.marketSelectWidth,
  },
  '& .sd-input-label': {
    ...theme.typography.label,
    color: theme.colors.text,
    marginBottom: theme.spacing.inputLabelGap,
  },
  [`@media (max-width: ${theme.breakpoints.mobile})`]: {
    '& .sd-container': {
      padding: `${theme.spacing.bodyPadding} 0`,
    },
    '& .sd-input-wrapper': {
      minWidth: 0,
      width: '100%',
    },
    '& .sd-region-wrapper, & .sd-market-wrapper': {
      width: '100%',
    },
  },
});

const REGION_DEFAULT = REGIONS.find((r) => r.value === REGION_ALL);

// Persist only the Region + Market portion of the filter — date fields are
// always derived from "today" on mount and shouldn't survive reloads.
const FILTERS_STORAGE_KEY = 'supervisor-dashboard:filters';

function readPersistedFilters() {
  try {
    const raw = window.localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function writePersistedFilters({ region, market }) {
  try {
    window.localStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({ region, market }),
    );
  } catch (e) {
    // Storage full or unavailable — non-fatal.
  }
}

// The Region dropdown uses short codes (MW/NE/WC/S) but `OpsLocation.region`
// (the FC→region source) returns full names. Map between them so the Region
// filter can narrow the Market dropdown to that region's FCs.
const REGION_CODE_TO_NAME = {
  MW: 'Midwest',
  NE: 'Northeast',
  WC: 'West',
  S: 'South',
};

function buildDefaultFilters() {
  const { startDate, endDate } = getDefaultDateRange(DEFAULT_DATE_RANGE_DAYS);
  const persisted = readPersistedFilters();
  return {
    startDate,
    endDate,
    region: persisted?.region || REGION_DEFAULT,
    market: persisted?.market || [],
  };
}

function SupervisorDashboardBody({ showRouteOperations }) {
  const [activeTab, setActiveTab] = useState(
    showRouteOperations ? TAB_IDS.ROUTE_OPERATIONS : TAB_IDS.MONITORING,
  );
  const isDispatchEnabled = useDispatchTabFlag();
  useEffect(() => {
    if (!isDispatchEnabled && activeTab === TAB_IDS.DISPATCH) {
      setActiveTab(
        showRouteOperations ? TAB_IDS.ROUTE_OPERATIONS : TAB_IDS.MONITORING,
      );
    }
  }, [isDispatchEnabled, activeTab, showRouteOperations]);
  // Monitoring tab: manual refresh bumps refetchKey (the 30s poll handles
  // live updates on its own); isMonitoringLoading + lastRefreshedAt drive
  // the refresh control's spinner and timestamp while Monitoring is active.
  const [isMonitoringLoading, setIsMonitoringLoading] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  // Monitoring stays idle (no data fetched) until the supervisor applies
  // filters on that tab; Clear All returns it to the idle prompt.
  const [monitoringApplied, setMonitoringApplied] = useState(false);
  const [draftFilters, setDraftFilters] = useState(buildDefaultFilters);
  const [committedFilters, setCommittedFilters] = useState(buildDefaultFilters);
  const [fcOptions, setFcOptions] = useState([]);
  const [fcByLocationId, setFcByLocationId] = useState({});
  // Human-readable name (opportunityName) per location id, used by the
  // Route Operations route cards as the primary stop label.
  const [nameByLocationId, setNameByLocationId] = useState({});
  // { [locationId]: { lat, long } } from OpsLocation.coordinates, used by the
  // Route Operations Missed Stops reattempt to fill MissedStopInput lat/long
  // (LocationRoute carries no coordinates).
  const [coordsByLocationId, setCoordsByLocationId] = useState({});
  // { [fulfillmentCenter]: regionCode } so a selected Region can narrow the
  // Market dropdown to that region's FCs (and drop out-of-region selections).
  const [fcToRegion, setFcToRegion] = useState({});
  const [isFcLoading, setIsFcLoading] = useState(false);
  // Bumped on every Apply click; the active tab includes it in its fetch
  // useEffect deps so Apply forces a fresh round-trip even when the
  // committed filter values haven't changed.
  const [refetchKey, setRefetchKey] = useState(0);
  // Bumped by Apply ALONE — not by Refresh or Clear All, which also bump
  // refetchKey. The Monitoring tab treats an Apply as a new question and
  // returns to its List view, which a refresh must never do.
  const [applyKey, setApplyKey] = useState(0);
  // Loading state surfaced by the active tab so the FilterBar can show
  // the "Applying…" spinner state and lock the Apply button mid-flight.
  const [isApplying, setIsApplying] = useState(false);
  // Wall-clock timestamp of the last Apply click, used by FilterBar to
  // render a "Last applied at HH:MM:SS" indicator. Null until first Apply.
  const [lastAppliedAt, setLastAppliedAt] = useState(null);
  // Board-wide refresh state — owned here so the button can sit above the
  // filter bar while the actual fetches live in RouteOperationsTab.
  // The tab bumps loading + refreshed-at back up via callbacks; clicking the
  // button increments the key, which the tab observes to trigger a refetch.
  const [boardRefreshKey, setBoardRefreshKey] = useState(0);
  const [isBoardRefreshing, setIsBoardRefreshing] = useState(false);
  const [lastBoardRefreshedAt, setLastBoardRefreshedAt] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const fetchFcs = async () => {
      setIsFcLoading(true);
      try {
        const allLocations = [];
        let nextToken = null;
        do {
          // eslint-disable-next-line no-await-in-loop
          const response = await OpsLocations({
            type: 'FRIDGE',
            nextToken,
          });
          if (!mountedRef.current) return;
          const locations = response?.data?.opsLocations?.locations || [];
          allLocations.push(...locations);
          nextToken = response?.data?.opsLocations?.nextToken;
        } while (nextToken);

        const idToFc = {};
        const idToName = {};
        const idToCoords = {};
        const fcRegion = {};
        const uniqueFcs = new Set();
        allLocations.forEach((loc) => {
          if (
            loc.id &&
            loc.fulfillmentCenter &&
            loc.fulfillmentCenter !== 'Does Not Exist'
          ) {
            idToFc[loc.id] = loc.fulfillmentCenter;
            uniqueFcs.add(loc.fulfillmentCenter);
            if (loc.region) fcRegion[loc.fulfillmentCenter] = loc.region;
          }
          if (loc.id && loc.opportunityName) {
            idToName[loc.id] = loc.opportunityName;
          }
          const lat = loc.coordinates?.latitude;
          const long = loc.coordinates?.longitude;
          if (loc.id && Number.isFinite(lat) && Number.isFinite(long)) {
            idToCoords[loc.id] = { lat, long };
          }
        });
        const opts = [...uniqueFcs]
          .sort((a, b) => a.localeCompare(b))
          .map((fc) => ({ value: fc, label: fc }));
        if (!mountedRef.current) return;
        setFcByLocationId(idToFc);
        setNameByLocationId(idToName);
        setCoordsByLocationId(idToCoords);
        setFcToRegion(fcRegion);
        setFcOptions(opts);
      } catch (e) {
        if (!mountedRef.current) return;
        console.error('SupervisorDashboard FC fetch failed:', e);
        toast.error(
          'Failed fetching fulfillment centers. Please try again.',
          toastOpts,
        );
      } finally {
        if (mountedRef.current) {
          setIsFcLoading(false);
        }
      }
    };
    fetchFcs();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const validation = useMemo(
    () =>
      validateRange(
        draftFilters.startDate,
        draftFilters.endDate,
        DATE_RANGE_LIMIT_DAYS,
      ),
    [draftFilters.startDate, draftFilters.endDate],
  );

  // Apply Filters stays clickable whenever the date range is valid, even
  // when there's no pending draft change — supervisors expect to be able
  // to "re-apply" a selection without first dirtying it.
  const applyDisabled = !validation.valid;

  // Persist Region + Market on every commit so a refresh restores the
  // supervisor's last-applied filter (date is always re-derived from today).
  useEffect(() => {
    writePersistedFilters({
      region: committedFilters.region,
      market: committedFilters.market,
    });
  }, [committedFilters.region, committedFilters.market]);

  const handleStartChange = useCallback((startDate) => {
    setDraftFilters((prev) => ({ ...prev, startDate }));
  }, []);

  const handleEndChange = useCallback((endDate) => {
    setDraftFilters((prev) => ({ ...prev, endDate }));
  }, []);

  // Monitoring and Dispatch are single-day views, so their one date picker
  // writes the same value to both startDate/endDate — keeping the
  // committed-filter shape and the Apply flow identical to the range-based
  // tabs.
  const handleSingleDateChange = useCallback((date) => {
    setDraftFilters((prev) => ({ ...prev, startDate: date, endDate: date }));
  }, []);

  // Single-day tabs cover a recent past day, today, or tomorrow — overnight
  // shifts deliver against the NEXT stocking date (1–2am drops), so night
  // supervisors need tomorrow's routes while drivers are out. Dispatch looks
  // back less far than Monitoring because it writes, not just reads.
  const isDispatch = activeTab === TAB_IDS.DISPATCH;
  const singleDateBounds = useMemo(() => {
    const lookback = isDispatch
      ? DISPATCH_DATE_LOOKBACK_DAYS
      : MONITORING_DATE_LOOKBACK_DAYS;
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + SINGLE_DATE_LOOKAHEAD_DAYS);
    const minDate = new Date();
    minDate.setDate(minDate.getDate() - lookback);
    return { minDate, maxDate };
  }, [isDispatch]);

  // A date applied on Monitoring can be older than Dispatch allows. Snap both
  // draft and committed dates back to today so the tab never fetches, or
  // lets a supervisor mark, a day outside its window.
  useEffect(() => {
    if (!isDispatch) return;
    const floor = new Date(singleDateBounds.minDate);
    floor.setHours(0, 0, 0, 0);
    const resetIfStale = (filters) => {
      if (filters.startDate >= floor) return filters;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return { ...filters, startDate: today, endDate: today };
    };
    setDraftFilters(resetIfStale);
    setCommittedFilters(resetIfStale);
  }, [isDispatch, singleDateBounds]);

  // Monitoring is always scoped to a specific region, so "All Regions" is
  // dropped from its picker (other tabs keep it). While the region is still the
  // shared "All Regions" default, show no selection so the supervisor picks one.
  const isMonitoring = activeTab === TAB_IDS.MONITORING;
  const usesSingleDate = isMonitoring || isDispatch;
  const regionOptions = useMemo(
    () =>
      isMonitoring ? REGIONS.filter((r) => r.value !== REGION_ALL) : REGIONS,
    [isMonitoring],
  );
  const regionValue =
    isMonitoring && draftFilters.region?.value === REGION_ALL
      ? null
      : draftFilters.region;

  // Selecting a specific Region narrows the Market dropdown to that region's
  // FCs, so a supervisor can't pick a market that conflicts with the region
  // (which silently emptied the result before). Graceful fallback: if the
  // FC→region map yields no matches (missing data / vocab mismatch), show all
  // FCs rather than an empty dropdown.
  const selectedRegionValue = draftFilters.region?.value;
  const selectedRegionName = REGION_CODE_TO_NAME[selectedRegionValue];
  const narrowMarket =
    Boolean(selectedRegionValue) && selectedRegionValue !== REGION_ALL;
  const marketOptions = useMemo(() => {
    if (!narrowMarket) return fcOptions;
    const inRegion = fcOptions.filter(
      (o) => fcToRegion[o.value] === selectedRegionName,
    );
    return inRegion.length > 0 ? inRegion : fcOptions;
  }, [narrowMarket, fcOptions, fcToRegion, selectedRegionName]);

  const handleRegionChange = useCallback(
    (region) => {
      setDraftFilters((prev) => {
        const nextRegionValue = region?.value;
        const nextRegionName = REGION_CODE_TO_NAME[nextRegionValue];
        const narrow =
          Boolean(nextRegionValue) && nextRegionValue !== REGION_ALL;
        // Drop any selected market whose FC is known to belong to a different
        // region. Unmapped FCs are kept (we don't know their region).
        const nextMarket = narrow
          ? (prev.market || []).filter(
              (m) =>
                !fcToRegion[m.value] || fcToRegion[m.value] === nextRegionName,
            )
          : prev.market;
        return { ...prev, region, market: nextMarket };
      });
    },
    [fcToRegion],
  );

  const handleMarketChange = useCallback((market) => {
    setDraftFilters((prev) => ({ ...prev, market: market || [] }));
  }, []);

  const handleApply = useCallback(() => {
    if (!validation.valid) return;
    setCommittedFilters(draftFilters);
    setLastAppliedAt(new Date());
    setRefetchKey((k) => k + 1);
    setApplyKey((k) => k + 1);
    // Arm the Monitoring tab on the first Apply made from it.
    if (activeTab === TAB_IDS.MONITORING) setMonitoringApplied(true);
  }, [draftFilters, validation.valid, activeTab]);

  const handleClearAll = useCallback(() => {
    const defaults = buildDefaultFilters();
    setDraftFilters(defaults);
    setCommittedFilters(defaults);
    setLastAppliedAt(null);
    setRefetchKey((k) => k + 1);
    setMonitoringApplied(false);
  }, []);

  const hiddenTabIds = [
    !showRouteOperations && TAB_IDS.ROUTE_OPERATIONS,
    !isDispatchEnabled && TAB_IDS.DISPATCH,
  ].filter(Boolean);
  const visibleTabs = TABS.filter((tab) => !hiddenTabIds.includes(tab.id));

  const renderActiveTab = () => {
    switch (activeTab) {
      case TAB_IDS.PLANNING:
        return <PlanningTab />;
      case TAB_IDS.ROUTE_OPERATIONS:
        return (
          <RouteOperationsTab
            region={committedFilters.region}
            market={committedFilters.market}
            fcByLocationId={fcByLocationId}
            nameByLocationId={nameByLocationId}
            coordsByLocationId={coordsByLocationId}
            refetchKey={refetchKey}
            onLoadingChange={setIsApplying}
            boardRefreshKey={boardRefreshKey}
            onBoardRefreshLoadingChange={setIsBoardRefreshing}
            onBoardRefreshed={setLastBoardRefreshedAt}
          />
        );
      case TAB_IDS.MONITORING:
        return (
          <MonitoringTab
            region={committedFilters.region}
            market={committedFilters.market}
            fcByLocationId={fcByLocationId}
            stockingDate={committedFilters.startDate}
            enabled={monitoringApplied}
            refetchKey={refetchKey}
            applyKey={applyKey}
            onLoadingChange={setIsMonitoringLoading}
            onApplyingChange={setIsApplying}
          />
        );
      case TAB_IDS.DISPATCH:
        return (
          <DispatchTab
            region={committedFilters.region}
            market={committedFilters.market}
            fcByLocationId={fcByLocationId}
            nameByLocationId={nameByLocationId}
            stockingDate={committedFilters.startDate}
            refetchKey={refetchKey}
            onLoadingChange={setIsApplying}
            boardRefreshKey={boardRefreshKey}
            onBoardRefreshLoadingChange={setIsBoardRefreshing}
            onBoardRefreshed={setLastBoardRefreshedAt}
          />
        );
      case TAB_IDS.EXCEPTION_REVIEW:
        return <ExceptionReviewTab />;
      default:
        return null;
    }
  };

  return (
    <StyledDiv>
      <div className="sd-container">
        <div className="sd-page-header-row">
          <h1 className="sd-page-header">Supervisor Dashboard</h1>
          {activeTab === TAB_IDS.MONITORING ? (
            <StatusRefreshControl
              isLoading={isMonitoringLoading}
              lastRefreshedAt={lastRefreshedAt}
              onRefresh={() => {
                setRefetchKey((k) => k + 1);
                setLastRefreshedAt(new Date());
              }}
            />
          ) : (
            <StatusRefreshControl
              isLoading={isBoardRefreshing}
              lastRefreshedAt={lastBoardRefreshedAt}
              onRefresh={() => setBoardRefreshKey((k) => k + 1)}
            />
          )}
        </div>

        <div className="sd-tabs-row">
          <Tabs
            tabs={visibleTabs}
            activeId={activeTab}
            onChange={setActiveTab}
          />
        </div>

        <div className="sd-filter-row-wrapper">
          <FilterBar
            onApply={handleApply}
            onClearAll={handleClearAll}
            applyDisabled={applyDisabled}
            isApplying={isApplying}
            lastAppliedAt={lastAppliedAt}
          >
            <div className="sd-input-wrapper sd-region-wrapper">
              <div className="sd-input-label">Region</div>
              <SingleSelect
                options={regionOptions}
                value={regionValue}
                onChange={handleRegionChange}
                width="100%"
              />
            </div>
            <div className="sd-input-wrapper sd-market-wrapper">
              <div className="sd-input-label">Market</div>
              <MultiSelect
                options={marketOptions}
                value={draftFilters.market}
                onChange={handleMarketChange}
                isLoading={isFcLoading}
                isDisabled={isFcLoading}
                getOptionLabel={(o) => o.label}
                getOptionValue={(o) => o.value}
                maxMenuHeight={400}
                width="100%"
              />
            </div>
            {usesSingleDate && (
              <div className="sd-input-wrapper">
                <div className="sd-input-label">Date</div>
                <DateField
                  value={draftFilters.startDate}
                  onChange={handleSingleDateChange}
                  minDate={singleDateBounds.minDate}
                  maxDate={singleDateBounds.maxDate}
                />
              </div>
            )}
            {!usesSingleDate && activeTab !== TAB_IDS.ROUTE_OPERATIONS && (
              <div className="sd-input-wrapper">
                <div className="sd-input-label">Date Range</div>
                <DateRangeField
                  startValue={draftFilters.startDate}
                  endValue={draftFilters.endDate}
                  onStartChange={handleStartChange}
                  onEndChange={handleEndChange}
                  error={validation.valid ? '' : validation.error}
                />
              </div>
            )}
          </FilterBar>
        </div>

        {renderActiveTab()}
      </div>
    </StyledDiv>
  );
}

SupervisorDashboardBody.propTypes = {
  showRouteOperations: PropTypes.bool.isRequired,
};

// LaunchDarkly gate for the Route Operations tab only — the dashboard itself
// is available to everyone. App.js initializes one LD client on auth-state
// change and threads the resolved `supervisor-dashboard-route-operations`
// value down via childProps. Three states:
//   `null`   — LD hasn't responded yet; render a minimal placeholder so the
//              tab list and default tab don't shift once the flag resolves.
//   `false`  — flag is off; render the dashboard without Route Operations.
//   `true`   — render the dashboard with all tabs.
// The body is its own component so hooks aren't conditionally called when
// the flag flips during the LD response.
function SupervisorDashboard({ hasAccessToRouteOperations }) {
  if (hasAccessToRouteOperations === null) {
    return (
      <div
        style={{ padding: theme.spacing.bodyPadding }}
        data-testid="sd-flag-loading"
      >
        Loading…
      </div>
    );
  }
  return (
    <SupervisorDashboardBody
      showRouteOperations={hasAccessToRouteOperations === true}
    />
  );
}

SupervisorDashboard.propTypes = {
  // PropTypes.bool can't express the null sentinel for "LD pending"; the
  // gate handles it explicitly via the tri-state checks above. defaultProps
  // captures the loading state.
  hasAccessToRouteOperations: PropTypes.bool,
};

SupervisorDashboard.defaultProps = {
  hasAccessToRouteOperations: null,
};

export default SupervisorDashboard;
